/**
 * A Lordicon animated mark.
 *
 * Wraps the `lord-icon` custom element and feeds it decoded icon data rather
 * than a `src`, because Lordicon's `.lottie` downloads are masked and the
 * element cannot parse them itself. See `lordiconAsset`.
 *
 * Renders nothing until the icon resolves, and nothing at all if it fails, so a
 * caller can put a fallback behind it and never show an empty box.
 */
import { useEffect, useRef, useState } from "react";
import { loadLordiconAsset } from "@/lib/lordiconAsset";

export default function LordIcon({
  src,
  trigger = "loop",
  target,
  colors,
  stroke,
  className,
  onUnavailable,
}: {
  /** Path to the `.lottie` (or plain `.json`) icon. */
  src: string;
  trigger?: string;
  /** Selector for the ancestor whose hover drives the icon. */
  target?: string;
  colors?: string;
  stroke?: string;
  className?: string;
  /** Called when the icon cannot be loaded, so the caller can fall back. */
  onUnavailable?: () => void;
}) {
  const elementRef = useRef<HTMLElement | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);

    loadLordiconAsset(src)
      .then(iconData => {
        if (cancelled) return;
        const element = elementRef.current as (HTMLElement & { icon?: unknown }) | null;
        if (!element) return;
        element.icon = iconData;

        /*
         * Rebuild the trigger now that there is something to play.
         *
         * The element creates its trigger when it connects, which happens before
         * this fetch resolves. A `loop` trigger built against no icon data draws
         * the first frame and then sits still -- the icon looked loaded and was
         * simply frozen. Re-setting the attribute runs `triggerChanged` again
         * against real data.
         */
        const trigger = element.getAttribute("trigger");
        if (trigger) {
          element.removeAttribute("trigger");
          element.setAttribute("trigger", trigger);
        }

        setReady(true);
      })
      .catch(() => {
        if (!cancelled) onUnavailable?.();
      });

    return () => {
      cancelled = true;
    };
    // `onUnavailable` is intentionally not a dependency: callers pass an inline
    // closure, and re-running the fetch on every render would defeat the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  return (
    <lord-icon
      ref={elementRef as never}
      className={className}
      trigger={trigger}
      target={target}
      colors={colors}
      stroke={stroke}
      style={{ opacity: ready ? 1 : 0, transition: "opacity .3s ease" }}
    />
  );
}
